import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BusinessTheme {
  id: string;
  business_id: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  heading_font: string;
  body_font: string;
  source_url: string | null;
  extracted_at: string | null;
  manually_customized: boolean;
  created_at: string;
  updated_at: string;
}

interface UseBusinessThemeOptions {
  businessId?: string;
  autoFetch?: boolean;
}

interface UseBusinessThemeReturn {
  theme: BusinessTheme | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  extractTheme: (websiteUrl: string) => Promise<BusinessTheme | null>;
  updateTheme: (updates: Partial<BusinessTheme>) => Promise<BusinessTheme | null>;
  extracting: boolean;
}

export function useBusinessTheme({
  businessId,
  autoFetch = true
}: UseBusinessThemeOptions = {}): UseBusinessThemeReturn {
  const [theme, setTheme] = useState<BusinessTheme | null>(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTheme = useCallback(async () => {
    if (!businessId) {
      setTheme(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('business_themes')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No theme found - not an error
          setTheme(null);
        } else {
          throw fetchError;
        }
      } else {
        setTheme(data as BusinessTheme);
      }
    } catch (err) {
      console.error('Error fetching business theme:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch theme');
      setTheme(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const extractTheme = useCallback(async (websiteUrl: string): Promise<BusinessTheme | null> => {
    if (!businessId) {
      setError('Business ID is required');
      return null;
    }

    setExtracting(true);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('extract-business-theme', {
        body: { businessId, websiteUrl }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to extract theme');
      }

      const result = response.data;
      if (!result.success) {
        throw new Error(result.error || 'Theme extraction failed');
      }

      setTheme(result.theme);
      return result.theme;
    } catch (err) {
      console.error('Error extracting theme:', err);
      setError(err instanceof Error ? err.message : 'Failed to extract theme');
      return null;
    } finally {
      setExtracting(false);
    }
  }, [businessId]);

  const updateTheme = useCallback(async (updates: Partial<BusinessTheme>): Promise<BusinessTheme | null> => {
    if (!businessId) {
      setError('Business ID is required');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        .from('business_themes')
        .upsert({
          business_id: businessId,
          ...updates,
          manually_customized: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'business_id'
        })
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      setTheme(data as BusinessTheme);
      return data as BusinessTheme;
    } catch (err) {
      console.error('Error updating theme:', err);
      setError(err instanceof Error ? err.message : 'Failed to update theme');
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (autoFetch && businessId) {
      fetchTheme();
    }
  }, [autoFetch, businessId, fetchTheme]);

  return {
    theme,
    loading,
    error,
    refetch: fetchTheme,
    extractTheme,
    updateTheme,
    extracting
  };
}

// Helper to convert theme to CSS variables
export function themeToCSSVariables(theme: BusinessTheme): Record<string, string> {
  return {
    '--business-primary': theme.primary_color,
    '--business-secondary': theme.secondary_color,
    '--business-accent': theme.accent_color,
    '--business-background': theme.background_color,
    '--business-text': theme.text_color,
    '--business-heading-font': theme.heading_font,
    '--business-body-font': theme.body_font,
  };
}

// Helper to generate Google Fonts URL
export function generateGoogleFontsUrl(theme: BusinessTheme): string | null {
  const fonts = new Set<string>();

  if (theme.heading_font && theme.heading_font !== 'Inter') {
    fonts.add(theme.heading_font);
  }
  if (theme.body_font && theme.body_font !== 'Inter') {
    fonts.add(theme.body_font);
  }

  if (fonts.size === 0) return null;

  const fontFamilies = Array.from(fonts)
    .map(f => f.replace(/\s+/g, '+') + ':wght@400;500;600;700')
    .join('&family=');

  return `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
}
